import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ConsentService } from './consent.service';
import { ConsentState } from './consent.types';

/**
 * Loads the third-party tags the visitor's cookie choice allows.
 *
 * - Google Tag Manager (GA4) is loaded by index.html, which also sets the
 *   Google Consent Mode default from the saved choice. This service pushes
 *   every later change to Consent Mode, so GA4 and the Google Ads
 *   conversions in AdsConversionService follow it.
 * - RB2B visitor identification is loaded here, and only while `ads` is
 *   allowed. While the banner waits for a first-time visitor's answer it
 *   holds back for HOLD_MS of visible time, so they can reject first; then,
 *   or as soon as they accept or move on to a second page, it loads under
 *   the opt-out default. A visitor who rejects is never sent to RB2B.
 *
 * Tags that are already running cannot be stopped reliably, so a rejection
 * after they loaded clears their cookies and reloads the page; the saved
 * choice keeps them off from then on.
 */
@Injectable({ providedIn: 'root' })
export class TagConsentService {
    private static readonly RB2B_KEY = '0OV0VH9E9G6Z';

    /** How long the banner may stay unanswered before RB2B loads anyway. */
    private static readonly HOLD_MS = 5000;

    private readonly isBrowser: boolean;
    private started = false;
    private rb2bLoaded = false;
    private pageViews = 0;
    private holdElapsed = false;

    constructor(
        private consent: ConsentService,
        private router: Router,
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
    }

    /** Call once from the root component. */
    start(): void {
        if (!this.isBrowser || this.started) return;
        this.started = true;

        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                this.pageViews++;
                // Reaching a second page without answering accepts the opt-out default.
                if (this.pageViews === 2) this.loadAllowed(this.consent.state);
            });

        this.consent.state$.subscribe(grants => {
            this.updateGoogleConsent(grants);
            if (this.mustReload(grants)) {
                this.clearTrackingCookies(grants);
                this.document.location.reload();
                return;
            }
            this.clearTrackingCookies(grants);
            this.loadAllowed(grants);
        });

        if (this.consent.needsChoice) this.startHoldTimer();
    }

    private get held(): boolean {
        return this.consent.needsChoice && this.pageViews < 2 && !this.holdElapsed;
    }

    /**
     * Counts HOLD_MS of time the page is actually on screen, so a tab opened
     * in the background is not identified before the visitor has seen the
     * banner. When it runs out the opt-out default applies; a rejection in the
     * meantime has already set `ads` to false, so nothing loads.
     */
    private startHoldTimer(): void {
        let remaining = TagConsentService.HOLD_MS;
        let startedAt = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const finish = () => {
            this.document.removeEventListener('visibilitychange', onVisibility);
            this.holdElapsed = true;
            this.loadAllowed(this.consent.state);
        };
        const run = () => {
            startedAt = Date.now();
            timer = setTimeout(finish, remaining);
        };
        const onVisibility = () => {
            if (this.document.visibilityState === 'visible') {
                if (!timer) run();
            } else if (timer) {
                clearTimeout(timer);
                timer = undefined;
                remaining -= Date.now() - startedAt;
            }
        };

        this.document.addEventListener('visibilitychange', onVisibility);
        if (this.document.visibilityState === 'visible') run();
    }

    private loadAllowed(grants: ConsentState): void {
        if (!this.consent.applies || !grants.ads || this.held) return;
        this.loadRb2b();
    }

    /** RB2B's own install snippet, unchanged apart from running on demand. */
    private loadRb2b(): void {
        const w = window as any;
        if (this.rb2bLoaded || w.reb2b) return;
        this.rb2bLoaded = true;
        w.reb2b = { loaded: true };
        const key = TagConsentService.RB2B_KEY;
        const s = this.document.createElement('script');
        s.async = true;
        s.src = 'https://ddwl4m2hdecbv.cloudfront.net/b/' + key + '/' + key + '.js.gz';
        const first = this.document.getElementsByTagName('script')[0];
        first.parentNode!.insertBefore(s, first);
    }

    /**
     * True when the choice switched off something already running: RB2B
     * after `ads` was denied, or Google Tag Manager after everything was.
     */
    private mustReload(grants: ConsentState): boolean {
        if (!grants.ads && this.rb2bLoaded) return true;
        const gtmLoaded = !!(window as any).google_tag_manager;
        return !grants.ads && !grants.analytics && gtmLoaded;
    }

    private updateGoogleConsent(grants: ConsentState): void {
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        // Consent Mode reads gtag-style `arguments` objects from the dataLayer.
        (function cm(..._args: any[]) { w.dataLayer.push(arguments); })('consent', 'update', {
            ad_storage: grants.ads ? 'granted' : 'denied',
            ad_user_data: grants.ads ? 'granted' : 'denied',
            ad_personalization: grants.ads ? 'granted' : 'denied',
            analytics_storage: grants.analytics ? 'granted' : 'denied',
        });
    }

    /**
     * Expire the first-party cookies set by the tags the visitor has just
     * switched off, on this host and on every parent domain they may use.
     */
    private clearTrackingCookies(grants: ConsentState): void {
        const patterns: RegExp[] = [];
        if (!grants.ads) patterns.push(/^_gcl_/, /^_reb2b/i, /^reb2b/i);
        if (!grants.analytics) patterns.push(/^_ga$/, /^_ga_/);
        if (!patterns.length) return;

        const parts = window.location.hostname.split('.');
        const domains = [''];
        for (let i = 0; i < parts.length - 1; i++) domains.push('; domain=.' + parts.slice(i).join('.'));

        this.document.cookie.split('; ').map(c => c.split('=')[0]).forEach(name => {
            if (!patterns.some(p => p.test(name))) return;
            domains.forEach(d => {
                this.document.cookie = `${name}=; path=/; max-age=0${d}`;
            });
        });
    }
}
